import axiosInstance from "../axiosInstance";

export const returnsAnalysisService = {
  /**
   * Dedicated Returns Analysis dashboard aggregation (/api/metrics/returns-analysis).
   * Server resolves client from JWT when present; client_id required for admins.
   * `channels` is zero or more exact coalesced Order Source values (Sales
   * Analysis's `source_channel || oms_provider` convention) — omit / empty for
   * "All Channels". Serialized the same way Sales Analysis serializes its own
   * multi-select Order Source filter (`channel[$in][]=...`).
   */
  fetch: async ({ clientId, startDate, endDate, channels = [] } = {}) => {
    const params = new URLSearchParams();
    if (clientId) params.set("client_id", clientId);
    if (startDate) params.set("start_date", startDate);
    if (endDate) params.set("end_date", endDate);
    (channels || [])
      .filter((value) => value != null && value !== "")
      .forEach((value) => {
        params.append("channel[$in][]", value);
      });
    const response = await axiosInstance.get(
      `/metrics/returns-analysis?${params.toString()}`,
    );
    return response.data;
  },

  /**
   * Lazy Open Return Age drilldown for one age bucket.
   * Same client / date / channel filters as `fetch`. Does not run on page load.
   * `stage` filters the table only — the bucket summary is always unfiltered.
   */
  fetchOpenAge: async ({
    clientId,
    startDate,
    endDate,
    channels = [],
    ageBucket,
    stage,
    skip = 0,
    limit = 25,
    sortField = "age_days",
    sortDir = "desc",
  } = {}) => {
    const params = new URLSearchParams();
    if (clientId) params.set("client_id", clientId);
    if (startDate) params.set("start_date", startDate);
    if (endDate) params.set("end_date", endDate);
    (channels || [])
      .filter((value) => value != null && value !== "")
      .forEach((value) => {
        params.append("channel[$in][]", value);
      });
    if (ageBucket) params.set("age_bucket", ageBucket);
    if (stage) params.set("stage", stage);
    params.set("$skip", String(skip));
    params.set("$limit", String(limit));
    if (sortField) params.set("sort_field", sortField);
    if (sortDir) params.set("sort_dir", sortDir);
    const query = params.toString().replace(/age_bucket=60\+/g, "age_bucket=60%2B");
    const response = await axiosInstance.get(
      `/metrics/returns-analysis-open-age?${query}`,
    );
    return response.data;
  },

  /**
   * Lazy SKU return-reason drilldown. Same client / date / channel filters
   * as `fetch`. Does not run on page load — only when a SKU row is opened.
   */
  fetchSkuReasons: async ({
    clientId,
    startDate,
    endDate,
    channels = [],
    sku,
  } = {}) => {
    const params = new URLSearchParams();
    if (clientId) params.set("client_id", clientId);
    if (startDate) params.set("start_date", startDate);
    if (endDate) params.set("end_date", endDate);
    (channels || [])
      .filter((value) => value != null && value !== "")
      .forEach((value) => {
        params.append("channel[$in][]", value);
      });
    if (sku) params.set("sku", sku);
    const response = await axiosInstance.get(
      `/metrics/returns-analysis-sku-reasons?${params.toString()}`,
    );
    return response.data;
  },

  /**
   * Lazy evidence for one SKU + exact canonical raw reason.
   * Same client / date / channel filters as `fetch`. Does not run on SKU click.
   */
  fetchSkuReasonReturns: async ({
    clientId,
    startDate,
    endDate,
    channels = [],
    sku,
    reason,
    skip = 0,
    limit = 25,
    sortField = "requested_at",
    sortDir = "desc",
  } = {}) => {
    const params = new URLSearchParams();
    if (clientId) params.set("client_id", clientId);
    if (startDate) params.set("start_date", startDate);
    if (endDate) params.set("end_date", endDate);
    (channels || [])
      .filter((value) => value != null && value !== "")
      .forEach((value) => {
        params.append("channel[$in][]", value);
      });
    if (sku) params.set("sku", sku);
    if (reason) params.set("reason", reason);
    params.set("$skip", String(skip));
    params.set("$limit", String(limit));
    if (sortField) params.set("sort_field", sortField);
    if (sortDir) params.set("sort_dir", sortDir);
    const response = await axiosInstance.get(
      `/metrics/returns-analysis-sku-reason-returns?${params.toString()}`,
    );
    return response.data;
  },
};
